import { Action } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { call, put, race, select, take } from 'redux-saga/effects';
import i18n from 'i18n';
import { notification } from 'features/notifications/toast/notification';
import { customVirtualBackgroundUploadRequested } from 'features/virtual-backgrounds/actions';
import { selectVirtualBackgrounds } from 'features/virtual-backgrounds/selectors';
import { requestVirtualBackgroundUpload } from 'features/virtual-backgrounds/services';
import {
  VirtualBackgroundItem,
  VirtualBackgroundUploadAcceptedPayload,
} from 'features/virtual-backgrounds/types';
import {
  customBackgroundDeleted,
  customBackgroundUploadAccepted,
} from 'features/virtual-backgrounds/virtualBackgroundsSlice';
import { SignalingSocket } from 'services/signaling';
import { logger } from 'utils/logger';
import { uploadPresignedFile } from 'utils/upload/presignedUpload';

export function* persistCustomBackgroundSaga(
  action: ReturnType<typeof customVirtualBackgroundUploadRequested>
) {
  const { id: tempId, file } = action.payload;

  let accepted: VirtualBackgroundUploadAcceptedPayload;
  try {
    accepted = yield call(requestVirtualBackgroundUpload, {
      name: file.name,
      fileSize: file.size,
    });
  } catch (error) {
    logger.remote().error(`Virtual background upload rejected file=${file.name}`, error);
    notification(i18n.t('notifications:virtual_bg.persist_failed'));
    return;
  }

  // The user may have deleted the optimistic tile while the signaling request
  // was in flight. The tile was not yet persisted, so the delete saga sent
  // nothing to the server — this saga must do the cleanup itself.
  const backgrounds: VirtualBackgroundItem[] = yield select(selectVirtualBackgrounds);
  if (!backgrounds.some((bg) => bg.id === tempId)) {
    yield call(SignalingSocket.send, {
      event: 'deleteVirtualBackground',
      data: { id: accepted.id },
    });
    return;
  }

  yield put(customBackgroundUploadAccepted({ tempId, id: accepted.id }));

  const abortController = new AbortController();

  try {
    const { deleted } = yield race({
      uploaded: call(
        uploadPresignedFile,
        file,
        {
          storageUrl: accepted.storageUrl,
          callbackUrl: accepted.callbackUrl,
          callbackToken: accepted.callbackToken,
          callbackBody: {
            size: file.size,
            expiration: accepted.expirationTimestamp,
            file_id: accepted.id,
            file_name: accepted.storageFilename,
          },
        },
        { signal: abortController.signal }
      ),
      deleted: take(
        (dispatched: Action) =>
          customBackgroundDeleted.match(dispatched) && dispatched.payload.id === accepted.id
      ),
    });

    if (deleted) {
      // The item was persisted before deletion, so deletePersistedBackgroundSaga
      // already signaled the server — only abort the in-flight HTTP transfer.
      abortController.abort();
    }
  } catch (error) {
    Sentry.captureException(error);
    logger.remote().error(`Cannot upload virtual background file=${file.name}`, error);
    notification(i18n.t('notifications:virtual_bg.persist_failed'));
  }
}
